import { NextSeo } from 'next-seo'
import { useRouter } from 'next/router'

import { GlossaryFragment } from '@/generated/contentful'

import { getBaseUrl } from '@/lib/contentful/utils'

import { getSeoLocale } from '@/utils/locales/getSeoLocale'
import getLocalizedPathFromEnglish from '@/utils/locales/localized-routes/getLocalizedPathFromEnglish'
import { pageLocales } from '@/utils/locales/page-locales'
import { useUrl } from '@/utils/url/useUrl'

import { useEntry } from '../Glossary.hooks'

type Props = {
	entryItem: GlossaryFragment
}

export function GlossaryMeta({ entryItem }: Props) {
	const { locale = 'en-US', query } = useRouter()
	const entry = useEntry()
	const url = useUrl({ locale })
	const baseUrl = `${url?.protocol}//${url?.host}`

	let glossary = entryItem

	if ('error' in entry && !entryItem) return null
	if ('data' in entry) glossary = entry.data

	const { seoMetadata } = glossary

	const includeTitleTemplate = Boolean(seoMetadata?.includeTitleTemplate)
	const title = includeTitleTemplate ? `${seoMetadata?.title} | BigCommerce` : seoMetadata?.title
	return (
		<NextSeo
			title={title ?? undefined}
			description={seoMetadata?.description ?? undefined}
			canonical={seoMetadata?.canonicalUrl || `${baseUrl}/glossary/${query.slug}/`}
			languageAlternates={[
				...pageLocales.glossary.map((locale) => ({
					hrefLang: locale,
					href: `${getBaseUrl(locale)}${getLocalizedPathFromEnglish(locale, '/glossary/')}${
						query.slug
					}/`
				})),
				{
					hrefLang: 'x-default',
					href: `${getBaseUrl()}${getLocalizedPathFromEnglish(locale, '/glossary/')}${query.slug}/`
				}
			]}
			openGraph={{
				title: title ?? undefined,
				description: seoMetadata?.description ?? '',
				url: seoMetadata?.canonicalUrl || `${baseUrl}/glossary/${query.slug}/`,
				images: seoMetadata?.image?.url
					? [
							{
								url: seoMetadata?.image?.url,
								width: seoMetadata?.image?.width,
								height: seoMetadata?.image?.height,
								alt: 'Bigcommerce logo',
								type: 'image/jpeg'
							}
					  ]
					: undefined,
				locale: getSeoLocale(locale)
			}}
			nofollow={Boolean(seoMetadata?.nofollow)}
			noindex={Boolean(seoMetadata?.noindex)}
		/>
	)
}
